'use client';

import React, { useEffect, useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { 
  Wrench, Terminal, Cpu, X, Plus, AlertTriangle, 
  Zap, Activity, Database, Server, Shield, Network, 
  Settings, Gauge, Key, Lock, HelpCircle 
} from 'lucide-react';

function getIconForKeyword(keyword: string = '') {
  const k = keyword.toLowerCase();
  if (k.includes('speed') || k.includes('gauge') || k.includes('velocity')) return <Gauge className="w-5 h-5" />;
  if (k.includes('power') || k.includes('energy') || k.includes('zap')) return <Zap className="w-5 h-5" />;
  if (k.includes('network') || k.includes('connection') || k.includes('router')) return <Network className="w-5 h-5" />;
  if (k.includes('database') || k.includes('data') || k.includes('storage')) return <Database className="w-5 h-5" />;
  if (k.includes('server') || k.includes('host') || k.includes('node')) return <Server className="w-5 h-5" />;
  if (k.includes('security') || k.includes('shield') || k.includes('protect')) return <Shield className="w-5 h-5" />;
  if (k.includes('key') || k.includes('auth') || k.includes('token')) return <Key className="w-5 h-5" />;
  if (k.includes('lock') || k.includes('secure')) return <Lock className="w-5 h-5" />;
  if (k.includes('setting') || k.includes('config') || k.includes('options')) return <Settings className="w-5 h-5" />;
  if (k.includes('activity') || k.includes('monitor') || k.includes('health') || k.includes('status')) return <Activity className="w-5 h-5" />;
  if (k.includes('cpu') || k.includes('process') || k.includes('compute')) return <Cpu className="w-5 h-5" />;
  if (k.includes('terminal') || k.includes('shell') || k.includes('script') || k.includes('code')) return <Terminal className="w-5 h-5" />;
  if (k.includes('wrench') || k.includes('fix') || k.includes('repair') || k.includes('tool')) return <Wrench className="w-5 h-5" />;
  return <AlertTriangle className="w-5 h-5" />;
}

interface ComplexSystemRepairMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    brokenState?: any[];
    tools?: any[];
    brokenCode?: string;
    hints?: any[];
    _hasHints?: boolean;
  };
}

export default function ComplexSystemRepairMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: ComplexSystemRepairMechanicProps) {
  const actualMechanicId = mechanicId || id || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={<Wrench className="w-5 h-5" />}
      iconLabel="Complex System Repair — Debug and fix the broken system"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <ComplexSystemRepairMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}

function ComplexSystemRepairMechanicInner({ payload, submissionState, setAnswer }: any) {
  // If we have brokenCode, it's V2 (Code Repair)
  if (payload?.brokenCode) {
    return <CodeRepairView payload={payload} submissionState={submissionState} setAnswer={setAnswer} />;
  }

  // Otherwise, it's V1 (Sequence Puzzle)
  return <SequenceRepairView payload={payload} submissionState={submissionState} setAnswer={setAnswer} />;
}

// ----------------------------------------------------------------------
// V1: Sequence Puzzle View
// ----------------------------------------------------------------------
function SequenceRepairView({ payload, submissionState, setAnswer }: any) {
  const [sequence, setSequence] = useState<any[]>([]);
  
  // We explicitly expect the new visual array format for brokenState
  const brokenStateNodes = Array.isArray(payload?.brokenState) ? payload.brokenState : [];
  const tools = payload?.tools || [];

  useEffect(() => {
    if (sequence.length > 0) {
      setAnswer(sequence.map(t => t.id));
    } else {
      setAnswer(null);
    }
  }, [sequence, setAnswer]);

  const addToolToSequence = (tool: any) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    if (sequence.find(t => t.id === tool.id)) return;
    setSequence([...sequence, tool]);
  };

  const removeToolFromSequence = (toolId: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    setSequence(sequence.filter(t => t.id !== toolId));
  };

  return (
    <div className="flex flex-col gap-6">
      
      {/* 1. System Node Visual Grid (Diagnostic Monitor) */}
      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-bold text-destructive uppercase tracking-wide flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 animate-pulse" />
          System Failure Diagnostics
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {brokenStateNodes.length > 0 ? (
            brokenStateNodes.map((node: any, idx: number) => (
              <div key={idx} className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex flex-col gap-2 items-center text-center shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-destructive/40" />
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{node.title}</span>
                  <span className="text-sm font-bold text-destructive leading-tight">{String(node.value)}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-xs text-muted-foreground italic p-2 border border-dashed rounded-md bg-muted/10 text-center">
              No diagnostic data available (requires updated visual lesson schema).
            </div>
          )}
        </div>
      </div>

      {/* 2. Repair Sequence Bay */}
      <div className="flex flex-col gap-1.5">
        <h4 className="text-xs font-bold text-foreground/70 uppercase tracking-wide flex items-center gap-1.5">
          <Wrench className="w-3.5 h-3.5" />
          Repair Sequence
        </h4>
        <div className="min-h-[48px] p-2 rounded-lg border border-dashed border-border bg-muted/20 flex flex-wrap gap-2 items-center">
          {sequence.length === 0 ? (
            <span className="text-xs text-muted-foreground italic px-2">Select tools below to build your sequence...</span>
          ) : (
            sequence.map((tool, index) => {
              const originalIndex = tools.findIndex((t: any) => t.id === tool.id) + 1;
              return (
                <div key={tool.id} className="flex items-center gap-2">
                  <div className="bg-primary text-primary-foreground pl-2 pr-1 py-1 rounded-md text-sm font-bold flex items-center justify-center gap-2 shadow-sm">
                    <span className="opacity-80">[{originalIndex}]</span>
                    <span className="truncate max-w-[100px] text-xs">{tool.title || "Tool"}</span>
                    <button 
                      type="button"
                      onClick={() => removeToolFromSequence(tool.id)}
                      disabled={submissionState !== 'idle' && submissionState !== 'error'}
                      className="hover:bg-black/20 p-0.5 rounded transition-colors disabled:opacity-50 text-primary-foreground/70 hover:text-primary-foreground ml-1"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {index < sequence.length - 1 && (
                    <div className="w-4 h-[2px] bg-primary/40 rounded-full" />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 3. Toolkit (Visual Tool Cards) */}
      <div className="flex flex-col gap-1.5">
        <h4 className="text-xs font-bold text-foreground/70 uppercase tracking-wide flex items-center gap-1.5">
          <Cpu className="w-3.5 h-3.5" />
          Available Tools
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {tools.map((tool: any, idx: number) => {
            const isUsed = sequence.some((t: any) => t.id === tool.id);
            const toolNumber = idx + 1;
            const title = tool.title || tool.action.split(' ').slice(0, 3).join(' ') + '...';
            
            return (
              <button
                type="button"
                key={tool.id}
                disabled={(submissionState !== 'idle' && submissionState !== 'error') || isUsed}
                onClick={() => addToolToSequence(tool)}
                className={`text-left p-3 rounded-xl border flex flex-col gap-2 transition-all group relative overflow-hidden
                  ${isUsed 
                    ? 'border-border/40 bg-muted/30 opacity-40 cursor-not-allowed' 
                    : 'border-border bg-card hover:border-primary/50 hover:bg-muted/10 shadow-sm hover:shadow hover:-translate-y-0.5'
                  }`}
              >
                <div className="flex items-center justify-between w-full">
                   <div className="flex items-center gap-2">
                     <div className={`p-1.5 rounded-lg ${isUsed ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'} shrink-0`}>
                       {getIconForKeyword(tool.iconKeyword || title)}
                     </div>
                     <span className="font-bold text-sm text-foreground leading-tight">{title}</span>
                   </div>
                   <div className="text-[10px] font-bold text-muted-foreground px-1.5 py-0.5 rounded bg-muted">
                     {toolNumber}
                   </div>
                </div>
                
                <div className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                  {tool.action}
                </div>
                
                {!isUsed && (
                  <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="w-4 h-4 text-primary" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
}

// ----------------------------------------------------------------------
// V2: Code Editor Repair View
// ----------------------------------------------------------------------
function CodeRepairView({ payload, submissionState, setAnswer }: any) {
  const [code, setCode] = useState<string>(payload.brokenCode || "");
  const [revealedHints, setRevealedHints] = useState<number[]>([]);
  
  const hints = payload.hints || [];

  // Initialize the parent answer
  useEffect(() => {
    setAnswer(code);
  }, [code, setAnswer]);

  const revealNextHint = () => {
    if (revealedHints.length < hints.length) {
      setRevealedHints([...revealedHints, revealedHints.length]);
    }
  };

  // Determine editor styling based on backend submission state
  let editorBorderColor = "border-border";
  let editorBgColor = "bg-black/90";
  
  if (submissionState === 'error') {
    editorBorderColor = "border-destructive/80 shadow-[0_0_15px_rgba(255,0,0,0.3)]";
    editorBgColor = "bg-destructive/10";
  } else if (submissionState === 'success') {
    editorBorderColor = "border-green-500 shadow-[0_0_15px_rgba(0,255,0,0.3)]";
    editorBgColor = "bg-green-500/10";
  }

  return (
    <div className="flex flex-col gap-6">
      
      {/* 1. Interactive Code Editor */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-foreground/80 flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            Live Code Editor
          </h4>
          {submissionState === 'error' && (
             <span className="text-xs font-bold text-destructive animate-pulse">Compilation Error / Fix required</span>
          )}
          {submissionState === 'success' && (
             <span className="text-xs font-bold text-green-500">System Restored!</span>
          )}
        </div>
        
        <div className={`rounded-xl overflow-hidden relative border-2 transition-all duration-300 ${editorBorderColor}`}>
          <div className="bg-black/40 border-b border-border/30 px-4 py-2 flex items-center gap-2 text-xs text-muted-foreground font-mono">
            <span>root@repair-bay:~# ./fix-system.sh</span>
          </div>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={submissionState !== 'idle' && submissionState !== 'error'}
            className={`w-full min-h-[300px] p-4 font-mono text-sm leading-relaxed text-blue-300 outline-none resize-y ${editorBgColor} disabled:opacity-80`}
            spellCheck="false"
            placeholder="Write your fix here..."
          />
        </div>
      </div>

      {/* 2. Hints System */}
      {hints.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
             <h4 className="text-sm font-bold text-foreground/80 flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              Diagnostics & Hints
            </h4>
            {revealedHints.length < hints.length && (
              <button 
                type="button"
                onClick={revealNextHint}
                className="text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors"
              >
                Reveal Next Hint ({hints.length - revealedHints.length} remaining)
              </button>
            )}
          </div>
          
          <div className="grid gap-2">
            {hints.map((hint: string, index: number) => {
              const isRevealed = revealedHints.includes(index);
              return (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border flex gap-3 text-sm transition-all duration-500
                    ${isRevealed ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400' : 'bg-muted/30 border-dashed border-border/50 text-muted-foreground'}`}
                >
                  <span className="font-bold shrink-0 opacity-50">Hint 0{index + 1}:</span>
                  {isRevealed ? (
                    <span className="leading-relaxed">{hint}</span>
                  ) : (
                    <span className="italic">Encrypted. Click &apos;Reveal Next Hint&apos; to decrypt.</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
    </div>
  );
}
